import { type FC, useCallback, memo } from "react";
import { Card } from "@snapchat/snap-design-system";
import { useSelection } from "../../core/hooks/useSelection";
import { cardHeaderStyle, modalContainerStyle, modalStyle } from "../../common/styles";
import { LensListItemModel } from "../models";
import { ActionModal } from "../../common/components/ActionModal";
import { AddLensesModalText } from "./AddLensesModalText";
import { AddLensesTable } from "./AddLensesTable";

interface AddLensesModalProps {
    visible: boolean;
    onCancel: () => void;
    onAddLenses: (selected: LensListItemModel[]) => void;
    alreadySelectedIds: Set<string>;
}

export const AddLensesModal: FC<AddLensesModalProps> = memo(
    ({ visible, onCancel, onAddLenses, alreadySelectedIds }) => {
        const getKey = useCallback((item: LensListItemModel) => item.compoundId.id as string, []);
        const { selectedItems, toggleItemSelection, resetSelection } = useSelection<LensListItemModel>(getKey);

        const addLenses = useCallback(() => {
            onAddLenses([...selectedItems.values()]);
            onCancel();
        }, [onAddLenses, selectedItems, onCancel]);

        return (
            <ActionModal
                className={modalStyle}
                padBody={false}
                actionText={AddLensesModalText.Action}
                visible={visible}
                onCancel={onCancel}
                onAction={addLenses}
                didClose={resetSelection}
                isActionButtonDisabled={selectedItems.size <= 0}
                width={670}
                data-testid="add-lens-to-group-modal"
            >
                <Card
                    title={"Add Lenses to Group"}
                    padBody={false}
                    titleBorder={false}
                    subtitle={"Select any number of Lenses to add to this Lens Group"}
                    className={modalContainerStyle}
                    headerClassName={cardHeaderStyle}
                >
                    <div>
                        <AddLensesTable
                            onToggleSelection={toggleItemSelection}
                            selectedLenses={selectedItems}
                            alreadySelectedIds={alreadySelectedIds}
                        />
                    </div>
                </Card>
            </ActionModal>
        );
    },
);
